import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase, supabaseUrl, supabaseAnonKey } from '../utils/supabaseClient';

interface UserProfile {
    id: string;
    full_name: string;
    role: 'owner' | 'admin' | 'staff';
    tenant_id: string;
    phone?: string;
}

interface AuthContextType {
    user: User | null;
    session: Session | null;
    profile: UserProfile | null;
    loading: boolean;
    signOut: () => Promise<void>;
    refreshProfile: () => Promise<UserProfile | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const profileRef = React.useRef<UserProfile | null>(null);
    const tokenRef = React.useRef<string | undefined>(undefined);

    // Keep ref in sync
    useEffect(() => {
        profileRef.current = profile;
    }, [profile]);

    // Fetch profile using manual fetch to bypass potential SDK deadlocks
    const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
        try {
            // Profile fetch start

            // Token is set during auth initialization; using tokenRef.current here

            if (!tokenRef.current) {
                console.warn('[AuthContext] No access token found for manual fetch');
                return null;
            }

            const response = await fetch(`${supabaseUrl}/rest/v1/profiles?id=eq.${userId}&select=*`, {
                headers: {
                    'apikey': supabaseAnonKey,
                    'Authorization': `Bearer ${tokenRef.current}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[AuthContext] Manual fetch failed:', response.status, errorText);
                return null;
            }

            const data = await response.json();
            // Profile fetch success

            const profileData = data && data.length > 0 ? data[0] : null;
            // Profile resolved
            return profileData as UserProfile;
        } catch (error: any) {
            console.error('[AuthContext] Unexpected error in manual fetch:', error);
            return null;
        }
    };

    const refreshProfile = async () => {
        if (session?.user) {
            // Refreshing profile
            const userProfile = await fetchProfile(session.user.id);
            setProfile(userProfile);
            return userProfile;
        }
        return null;
    };

    useEffect(() => {
        // Guard against infinite loading - force complete after 15 seconds
        const loadingTimeout = setTimeout(() => {
            if (loading) {
                console.warn('[AuthContext] Forced loading complete after timeout');
                setLoading(false);
            }
        }, 15000);

        // Initial session check
        const initializeAuth = async () => {
            // Initializing auth
            try {
                const { data: { session: currentSession } } = await supabase.auth.getSession();
                if (currentSession) {
                    // Session found
                    setSession(currentSession);
                    setUser(currentSession.user);
                    tokenRef.current = currentSession.access_token;

                    const userProfile = await fetchProfile(currentSession.user.id);
                    setProfile(userProfile);
                }
            } catch (err) {
                console.error('[AuthContext] Error in initializeAuth:', err);
            } finally {
                setLoading(false);
                clearTimeout(loadingTimeout);
            }
        };

        // Listen for auth state changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
            // Auth state changed

            if (currentSession) {
                setSession(currentSession);
                setUser(currentSession.user);
                tokenRef.current = currentSession.access_token;

                // Only re-fetch if user changed or profile is missing
                if (!profileRef.current || profileRef.current.id !== currentSession.user.id) {
                    // Fetching profile after change
                    const userProfile = await fetchProfile(currentSession.user.id);
                    setProfile(userProfile);
                }
            } else {
                setSession(null);
                setUser(null);
                setProfile(null);
            }

            setLoading(false);
            clearTimeout(loadingTimeout);
        });

        initializeAuth();

        return () => {
            subscription.unsubscribe();
            clearTimeout(loadingTimeout);
        };
    }, []);

    const signOut = async () => {
        await supabase.auth.signOut();
        setProfile(null);
        setUser(null);
        setSession(null);
    };

    return (
        <AuthContext.Provider value={{ user, session, profile, loading, signOut, refreshProfile }}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook personalizado para usar o contexto
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth deve ser usado dentro de um AuthProvider');
    }
    return context;
};
